import { useOffer } from '@/providers/PriceFreezeOfferProvider';
import { selectOfferDuration } from '@/selectors';
import {
  createFeature,
  Desktop,
  Mobile,
  useFlow,
  useTranslation,
} from '@edreamsodigeo/frontend-core';
import { ACTIONS, usePFTracking } from '@/hooks/tracking';
import React, { useEffect, useRef, useState } from 'react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  BUYER_INFO,
  IS_ACTIVE_PRIME_USER,
  usePriceFreezeSession,
} from '@/hooks/usePriceFreezeSession';
import { useLogin } from '@/providers/login/LoginProvider';
import { useContactDetails } from '@/providers/UserInfoProvider';
import { useIsActivePrimeUser } from '@/providers/PrimeUserProvider';
import { SearchExpiredError } from '@/components/FunnelErrors';
import { VersionedPrimeInfoBanner } from '@/components/PrimeInfoBanner/PFPrimeInfoBanner';
import { Box, Flex } from '@edreamsodigeo/prisma-design-system';
import { VersionedIRUMPrimeHeader } from '@/components/HomePage/HomePagePrimeHeader';
import { PriceOffer } from '@/components/PriceOffer';
import {
  ItineraryDetailsIRUM,
  StyledNoFeeKey,
} from '@/components/HomePage/HomePageUtilsIRUM';
import { SummaryContactDetails } from '@/components/SummaryContactDetails';
import { MoreInfoLink } from '@/components/MoreInfoLink';
import {
  HomePageButtonContactDetails,
  VersionedHomeContactDetailsButton,
} from '@/components/HomePage/HomePageButtonContactDetails';
import { PrimePopOver } from '@/components/PrimePopOver';
import { PRIME_FIRST_PHASE_1 } from '@/versionedComponents';
import { withLoadPlaceholder } from '@/HOC/withDatamesh';

const DatameshTrackLoad = withLoadPlaceholder('home:load');

export function HomepageComponent({
  formFields,
  loading,
  error,
  validation = () => true,
  onContinue,
}) {
  const offer = useOffer();
  const days = selectOfferDuration(offer);
  const { t } = useTranslation();
  const { track, trackPage } = usePFTracking();
  const capValue = offer.cap;
  const flow = useFlow();

  const PrimeHeaderPaddingFromLogoValue =
    flow === 'desktop' ? '120px' : '1x_huge';

  useEffect(() => {
    trackPage('summary-details-extras');
  }, [trackPage]);

  useDocumentTitle(t('header.title.pricefreeze'));
  const [getSessionItem, setSessionItem] = usePriceFreezeSession();
  const buyerSessionData = getSessionItem(BUYER_INFO);
  const { user, isLogged } = useLogin();
  const contactDetails = useContactDetails();
  const [data, setData] = useState(
    () => contactDetails || buyerSessionData || user || {}
  );
  const contactDetailsFormRef = useRef(null);

  const [showErrors, setShowErrors] = useState(false);
  const {
    isActive,
    setUserEmail,
    userEmail = '',
    loading: isActiveLoading,
  } = useIsActivePrimeUser();

  const onBlur = (id) => (val) => {
    setData({ ...data, [id]: val });
    if (id === formFields.EMAIL.id) {
      setActiveUserEmail(id, val);
    }
  };
  const setActiveUserEmail = (id, val) => {
    if (val !== userEmail) {
      setUserEmail(val);
    }
  };
  const validate = () =>
    Object.values(formFields).every(({ id, validation }) => {
      return validation(data[id]);
    });

  const showInvalidForm = () => {
    setShowErrors(true);
    if (contactDetailsFormRef?.current) {
      window.scrollTo({
        top: contactDetailsFormRef.current?.offsetTop,
        behavior: 'smooth',
      });
    }
  };

  const onContinueButtonClick = () => {
    if (!validate() || !validation()) {
      return showInvalidForm();
    } else {
      track(ACTIONS.HOME.CLICK.CONTINUE);
      window.onbeforeunload = null;
      setSessionItem(BUYER_INFO, data);
      setSessionItem(IS_ACTIVE_PRIME_USER, isActive);

      onContinue(data);
    }
  };

  return (
    <>
      <DatameshTrackLoad />
      <SearchExpiredError open={error} />
      {!isLogged && <VersionedPrimeInfoBanner />}
      <Box backgroundColor="white" pb="2x_big" data-testid="homepage">
        <VersionedIRUMPrimeHeader
          PrimeHeaderPaddingFromLogoValue={PrimeHeaderPaddingFromLogoValue}
          days={days}
        />

        <LayoutByInterface
          offer={<PriceOffer noFeeKey={<StyledNoFeeKey />} />}
          contactDetails={
            <div ref={contactDetailsFormRef}>
              <SummaryContactDetails
                data={data}
                onBlur={onBlur}
                showErrors={showErrors}
                isActive={isActive}
                userEmail={userEmail}
                isActiveLoading={isActiveLoading}
                disabledEmail={
                  Boolean(contactDetails?.email) || Boolean(user?.email)
                }
                formFields={formFields}
              />
            </div>
          }
        />

        <Mobile>
          <ItineraryDetailsIRUM />
        </Mobile>
      </Box>
      <Flex mt="big" mb="2x_big" justifyContent="center">
        <MoreInfoLink cap={capValue} deposit={offer.deposit} days={days} />
      </Flex>
      <Mobile>
        <VersionedHomeContactDetailsButton
          disabledRecognizeUsers={!isActive && !isLogged}
          disabledPrimeAcquisition={false}
          disabledPrimeAcquisitionPhase2={false}
          onContinueHandler={onContinueButtonClick}
          loading={loading}
        />
      </Mobile>
      <Desktop>
        <HomePageButtonContactDetails
          loading={loading}
          disabled={!isActive && !isLogged}
          onContinueHandler={onContinueButtonClick}
        />
      </Desktop>
    </>
  );
}

const PrimePopOverComponent = () => {
  const { isPrime } = useLogin();
  const { t } = useTranslation();

  if (isPrime) {
    return null;
  }

  return (
    <Box pb="tiny">
      <Box
        paddingX={'base'}
        width={'100%'}
        style={{
          bottom: '0',
        }}
      >
        <PrimePopOver text={t('pricefreeze.primePopOver.text')} />
      </Box>
    </Box>
  );
};

const PrimePopOverComponentVariation = createFeature({
  name: PRIME_FIRST_PHASE_1,
  variations: [null, PrimePopOverComponent],
  version: 1,
});

const LayoutByInterface = ({ offer, contactDetails }) => (
  <>
    <Mobile>
      <PrimePopOverComponentVariation />
      <Box px="base">{offer}</Box>
      {contactDetails}
    </Mobile>
    <Desktop>
      <Flex>
        <Box ml="2x_big" mt="small" flex={1.7}>
          {offer}
        </Box>
        <Box flex={2}>{contactDetails}</Box>
      </Flex>
    </Desktop>
  </>
);
